/**
 * "Continue de onde parou" — módulos do curso ativo, com a última aula à frente.
 *
 * Não há tabela de progresso por aula no backend (`attendances` só tem POST),
 * então a ordenação usa a última aula aberta, guardada no cliente. O que se
 * promete na UI é exatamente isso: por onde o usuário andou, não uma barra de
 * progresso real.
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { GRADIENTS } from '../../config/courses';
import { getModulesByUserTopic, type ModuleItem } from '../../services/moduleService';
import { getVideoLessonsByUserTopic } from '../../services/videoLessonService';
import { getModuleImageSrc } from '../../utils/moduleImages';
import { readLastLesson } from '../../utils/lastLesson';
import CourseCarousel from './CourseCarousel';
import PlatformCard from './PlatformCard';

interface ContinueWatchingProps {
  /** Entra no curso ativo (o carrossel leva sempre às videoaulas). */
  onOpenCourse: () => void;
}

const ContinueWatching: React.FC<ContinueWatchingProps> = ({ onOpenCourse }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data: modules = [] } = useQuery({
    queryKey: ['modules'],
    queryFn: getModulesByUserTopic,
    staleTime: 1000 * 60 * 10,
  });

  // Mesmas query keys de VideoAulas.tsx de propósito: entrar no curso logo
  // depois reaproveita o cache em vez de refazer as duas chamadas.
  const { data: lessons = [] } = useQuery({
    queryKey: ['videoLessons'],
    queryFn: getVideoLessonsByUserTopic,
    staleTime: 1000 * 60 * 10,
  });

  const lessonCountByModule = useMemo(() => {
    const counts = new Map<number, number>();
    for (const lesson of lessons) {
      if (lesson.moduleId !== null) {
        counts.set(lesson.moduleId, (counts.get(lesson.moduleId) ?? 0) + 1);
      }
    }
    return counts;
  }, [lessons]);

  // O módulo da última aula assistida abre a fileira; o resto mantém a ordem
  // natural do curso.
  const orderedModules = useMemo(() => {
    const lastLesson = readLastLesson(user?.topicId);
    if (lastLesson?.moduleId == null) return modules;

    const index = modules.findIndex((m) => m.id === lastLesson.moduleId);
    if (index <= 0) return modules;

    const copy = [...modules];
    const [current] = copy.splice(index, 1);
    return [current, ...copy];
  }, [modules, user?.topicId]);

  const heroModuleId = orderedModules[0]?.id ?? -1;

  const subtitleFor = useMemo(
    () => (module: ModuleItem) => {
      const count = lessonCountByModule.get(module.id);
      if (!count) return null;

      const unit =
        count === 1 ? t('platform.lessonSingular') : t('platform.lessonPlural');
      return `${count} ${unit}`;
    },
    [lessonCountByModule, t],
  );

  if (orderedModules.length === 0) return null;

  return (
    <CourseCarousel title={t('platform.continueWatching')}>
      {orderedModules.map((module) => (
        <PlatformCard
          key={module.id}
          variant="thumb"
          title={module.name}
          subtitle={subtitleFor(module)}
          // Sem capa cadastrada, gradiente da paleta em vez do placeholder
          // genérico — ele é claro e abriria um buraco branco na home escura.
          image={module.coverUpdatedAt ? getModuleImageSrc(module) : undefined}
          gradient={GRADIENTS[module.id % GRADIENTS.length]}
          highlighted={module.id === heroModuleId}
          showPlayOnHover
          onClick={onOpenCourse}
        />
      ))}
    </CourseCarousel>
  );
};

export default ContinueWatching;
